import { useState, useRef, useEffect, useCallback } from 'react';
import ChatEngine from './chatEngine';
import './ChatBot.css';

const BOOKING_LINK = 'https://instacarspa.simplybook.me/v2/';
const chatEngine = new ChatEngine();

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [language, setLanguage] = useState(null);
  const messagesEndRef = useRef(null);
  const abandonmentTimer = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Show language selection on open
  useEffect(() => {
    if (isOpen && messages.length === 0 && !language) {
      setMessages([
        {
          type: 'bot',
          text: 'Welcome to InstaCarSpa! Please select your language:\n\nBienvenue chez InstaCarSpa! Veuillez choisir votre langue :',
          isLanguageSelect: true
        }
      ]);
    }
  }, [isOpen, messages.length, language]);

  const addBotMessage = useCallback((text, extras = {}) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { type: 'bot', text, ...extras }]);
      setIsTyping(false);
    }, 1000);
  }, []);

  // Abandonment recovery
  const resetAbandonmentTimer = useCallback(() => {
    if (abandonmentTimer.current) clearTimeout(abandonmentTimer.current);
    if (!language) return;
    abandonmentTimer.current = setTimeout(() => {
      const msg = chatEngine.getAbandonmentMessage();
      addBotMessage(msg, { showBooking: true });
    }, 60000); // 60 seconds of inactivity
  }, [language, addBotMessage]);

  useEffect(() => {
    return () => {
      if (abandonmentTimer.current) clearTimeout(abandonmentTimer.current);
    };
  }, []);

  const selectLanguage = (lang) => {
    setLanguage(lang);
    chatEngine.setLanguage(lang);

    const greeting = lang === 'en'
      ? 'Hi there! Welcome to InstaCarSpa. Are you looking for a car wash or detailing service? We offer a variety of options to keep your car sparkling clean. Just let me know what you need!'
      : 'Bonjour! Bienvenue chez InstaCarSpa. Cherchez-vous un service de lavage ou d\'esthétique automobile? Nous offrons une variété d\'options pour garder votre voiture étincelante. Dites-moi ce dont vous avez besoin!';

    setMessages((prev) => [
      ...prev,
      { type: 'user', text: lang === 'en' ? '🇨🇦 English' : '🇫🇷 Français' }
    ]);

    addBotMessage(greeting);
    resetAbandonmentTimer();
  };

  const handleSend = () => {
    if (!inputValue.trim() || !language) return;

    const userMessage = { type: 'user', text: inputValue };
    setMessages((prev) => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsTyping(true);
    resetAbandonmentTimer();

    setTimeout(() => {
      const result = chatEngine.getResponse(currentInput);
      const booking = chatEngine.getBookingInfo();

      const botMsg = {
        type: 'bot',
        text: result.text,
        showBooking: result.showBooking,
        bookingText: booking.text,
        bookingLink: booking.link,
        upsell: result.upsell,
        isEscalation: result.isEscalation
      };

      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 1200);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  const handleReset = () => {
    chatEngine.reset();
    setLanguage(null);
    setMessages([
      {
        type: 'bot',
        text: 'Welcome to InstaCarSpa! Please select your language:\n\nBienvenue chez InstaCarSpa! Veuillez choisir votre langue :',
        isLanguageSelect: true
      }
    ]);
  };

  const handleQuickReply = (text) => {
    setInputValue(text);
    setTimeout(() => {
      const fakeEvent = { key: 'Enter' };
      handleKeyPress(fakeEvent);
    }, 100);
  };

  const renderMessage = (msg, index) => {
    return (
      <div key={index} className={`chatbot__message chatbot__message--${msg.type}`}>
        {msg.type === 'bot' && (
          <img
            src="https://instacarspa.s3.us-east-1.amazonaws.com/9.png"
            alt="Bot"
            className="chatbot__message-avatar"
          />
        )}
        <div className="chatbot__message-content">
          <div className="chatbot__message-bubble">
            {msg.text.split('\n').map((line, i) => (
              <span key={i}>
                {line}
                {i < msg.text.split('\n').length - 1 && <br />}
              </span>
            ))}
          </div>

          {/* Language selection buttons */}
          {msg.isLanguageSelect && !language && (
            <div className="chatbot__quick-replies">
              <button
                className="chatbot__quick-reply"
                onClick={() => selectLanguage('en')}
              >
                🇨🇦 English
              </button>
              <button
                className="chatbot__quick-reply"
                onClick={() => selectLanguage('fr')}
              >
                🇫🇷 Français
              </button>
            </div>
          )}

          {/* Booking button */}
          {msg.showBooking && (
            <a
              href={msg.bookingLink || BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="chatbot__booking-btn"
            >
              {msg.bookingText || (language === 'fr' ? '👉 Réserver maintenant' : '👉 Book Now')}
            </a>
          )}

          {/* Upsell suggestions */}
          {msg.upsell && (
            <div className="chatbot__upsell">
              <p className="chatbot__upsell-text">{msg.upsell.message}</p>
              <div className="chatbot__quick-replies">
                {msg.upsell.suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    className="chatbot__quick-reply"
                    onClick={() => handleQuickReply(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Escalation contact buttons */}
          {msg.isEscalation && (
            <div className="chatbot__escalation-buttons">
              <a href="tel:+14383642535" className="chatbot__escalation-btn">
                📞 {language === 'fr' ? 'Appeler' : 'Call Now'}
              </a>
              <a
                href="https://wa.me/14383642535"
                target="_blank"
                rel="noopener noreferrer"
                className="chatbot__escalation-btn"
              >
                📲 WhatsApp
              </a>
              <a href="mailto:instacarspa@gmail.com" className="chatbot__escalation-btn">
                📧 {language === 'fr' ? 'Courriel' : 'Email Us'}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="chatbot">
      <div className={`chatbot__window ${isOpen ? 'chatbot__window--open' : ''}`}>
        <div className="chatbot__header">
          <div className="chatbot__header-info">
            <img
              src="https://instacarspa.s3.us-east-1.amazonaws.com/9.png"
              alt="InstaCarSpa"
              className="chatbot__avatar"
            />
            <span className="chatbot__title">InstaCarChatAI</span>
          </div>
          <div className="chatbot__header-actions">
            <button className="chatbot__action-btn" onClick={handleReset} aria-label="Reset">
              <svg viewBox="0 0 512 512" width="18" height="18">
                <path fill="currentColor" d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88c-77.458.068-144.328 53.178-162.791 126.85-1.344 5.363-6.122 9.15-11.651 9.15H24.103c-7.498 0-13.194-6.807-11.807-14.176C33.933 94.924 134.813 8 256 8c66.448 0 126.791 26.136 171.315 68.685L463.03 41.97c13.54-13.54 36.97-3.95 36.97 15.172V191.03c0 8.837-7.163 16-16 16H350.143c-19.122 0-28.712-23.43-15.172-36.97l35.749-35.78zM256 424c-77.458-.068-144.328-53.178-162.791-126.85-1.344-5.363-6.122-9.15-11.651-9.15H24.103c-7.498 0-13.194 6.807-11.807 14.176C33.933 417.076 134.813 504 256 504c66.448 0 126.791-26.136 171.315-68.685l35.715 35.715c13.54 13.54 36.97 3.95 36.97-15.172V320.97c0-8.837-7.163-16-16-16H350.143c-19.122 0-28.712 23.43-15.172 36.97l-35.749 35.78C269.458 407.992 229.888 424.038 256 424z"/>
              </svg>
            </button>
            <button className="chatbot__action-btn" onClick={() => setIsOpen(false)} aria-label="Close">
              <svg viewBox="0 0 352 512" width="16" height="16">
                <path fill="currentColor" d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="chatbot__messages">
          {messages.map((msg, index) => renderMessage(msg, index))}
          {isTyping && (
            <div className="chatbot__message chatbot__message--bot">
              <img
                src="https://instacarspa.s3.us-east-1.amazonaws.com/9.png"
                alt="Bot"
                className="chatbot__message-avatar"
              />
              <div className="chatbot__message-bubble chatbot__typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot__input-area">
          <input
            type="text"
            className="chatbot__input"
            placeholder={language === 'fr' ? 'Écrivez un message...' : 'Write a reply...'}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={!language}
          />
          <button className="chatbot__send-btn" onClick={handleSend} aria-label="Send" disabled={!language}>
            <svg viewBox="0 0 512 512" width="18" height="18">
              <path fill="currentColor" d="M476 3.2L12.5 270.6c-18.1 10.4-15.8 35.6 2.2 43.2L121 358.4l287.3-253.2c5.5-4.9 13.3 2.6 8.6 8.3L176 407v80.5c0 23.6 28.5 32.9 42.5 15.8L282 426l124.6 52.2c14.2 6 30.4-2.9 33-18.2l72-432C515 7.8 493.3-6.8 476 3.2z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Floating Button */}
      <button
        className={`chatbot__toggle ${isOpen ? 'chatbot__toggle--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open chat"
      >
        {isOpen ? (
          <svg viewBox="0 0 352 512" width="24" height="24">
            <path fill="currentColor" d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 512 512" width="28" height="28">
            <path fill="currentColor" d="M256 32C114.6 32 0 125.1 0 240c0 49.6 21.4 95 57 130.7C44.5 421.1 2.7 466 2.2 466.5c-2.2 2.3-2.8 5.7-1.5 8.7S4.8 480 8 480c66.3 0 116-31.8 140.6-51.4 32.7 12.3 69 19.4 107.4 19.4 141.4 0 256-93.1 256-208S397.4 32 256 32z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default ChatBot;
